import type { Raw, Reactive } from 'vue';
import type { ExtendedRulesDeclarations, ReglePartialRuleTree, RegleRoot } from '@regle/core';

// Based on example from https://github.com/victorgarciaesgi/regle/issues/140
export function useForm<
    TForm extends Record<string, any>,
    TRules extends ReglePartialRuleTree<TForm, Partial<ExtendedRulesDeclarations>>,
    TSubmitParams = any,
>(
    form: Ref<TForm> | Reactive<TForm>,
    rules: MaybeRef<TRules>,
    onSubmit: (params?: TSubmitParams) => Promise<void>,
): {
    r$: Raw<RegleRoot<TForm, TRules>>;
    errors: Ref<string[]>;
    loading: Ref<boolean>;
    submit: (params?: TSubmitParams) => Promise<void>;
    resetData: () => void;
} {
    const errors = ref<string[]>([]);
    const loading = ref(false);

    const { r$ } = useRegle(form, rules as any, { autoDirty: false } as any);

    const formValue = unref(form);
    const initialForm = structuredClone(toRaw(formValue));

    function resetData() {
        // Object.assign creates a shallow copy
        Object.assign(formValue, structuredClone(initialForm));
        r$.$reset();
    }

    async function submit(params?: TSubmitParams) {
        if (r$.$invalid) return;

        errors.value = [];
        loading.value = true;

        try {
            await onSubmit(params);
        } catch (error) {
            errors.value = getBadRequestErrorsOrThrow(error);
        } finally {
            loading.value = false;
        }
    }

    return { r$: r$ as any, errors, loading, submit, resetData };
}
